import { createContext, useCallback, useContext, useEffect, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "../lib/db-client";
import { useAuthContext } from "./AuthContext";
import { coreQueryKeys } from "../lib/query-keys";

// --- Types ---

export interface Profile {
    id: string;
    display_name: string | null;
    bio: string | null;
    avatar_url: string | null;
    created_at: string;
    updated_at: string;
}

export interface UpdateProfileInput {
    display_name?: string | null;
    bio?: string | null;
    avatar_url?: string | null;
}

export interface UserStats {
    totalSessions: number;
    totalGroups: number;
    publicSessions: number;
    accountCreated: string | null;
    lastActivity: string | null;
    totalTimeSeconds: number;
    topicCount: number;
}

/**
 * Profile identity only — deliberately not the user's aggregate stats.
 *
 * `UserStats` used to live on this context, which meant every page paid for it:
 * the provider is mounted at the app root, so opening a static page fired three
 * `practice_sessions` count queries, a `study_group_members` count, a full
 * `topic_time` read and a **second** `profiles` request (for `created_at`, which
 * the profile query had already returned). It is now `useUserStats()` — an
 * opt-in hook that only the profile dialog calls.
 */
interface ProfileContextType {
    profile: Profile | null;
    isLoading: boolean;
    error: Error | null;
    updateProfile: (updates: UpdateProfileInput) => Promise<Profile>;
    isUpdating: boolean;
    refreshProfile: () => Promise<void>;
}

// --- Context ---

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfileContext = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error("useProfileContext must be used within a ProfileProvider");
    }
    return context;
};

// --- Provider ---

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
    const { userId, isAuthenticated } = useAuthContext();
    const queryClient = useQueryClient();

    // Profile fetching function
    const fetchProfile = useCallback(async () => {
        if (!userId) {
            return null;
        }

        const { data, error: fetchError } = await db.from("profiles")
            .select("*")
            .eq("id", userId)
            .single();

        if (fetchError) {
            // If profile doesn't exist, create it
            if (fetchError.code === "PGRST116") {
                const { data: newProfile, error: insertError } = await db.from("profiles")
                    .insert({ id: userId })
                    .select()
                    .single();

                if (insertError) throw insertError;
                return newProfile as Profile;
            }
            throw fetchError;
        }

        return data as Profile;
    }, [userId]);

    // Profile query with React Query
    const { 
        data: profile, 
        isLoading, 
        error: queryError 
    } = useQuery({
        queryKey: coreQueryKeys.profile(userId),
        queryFn: fetchProfile,
        enabled: !!isAuthenticated && !!userId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10,   // 10 minutes
        retry: (failureCount, error: any) => {
            // Don't retry on auth errors
            if (error?.status >= 400 && error?.status < 500) return false;
            return failureCount < 3;
        },
    });

    // Profile update mutation
    const { mutateAsync: updateProfileMutation, isPending: isUpdating } = useMutation({
        mutationFn: async (updates: UpdateProfileInput): Promise<Profile> => {
            if (!userId) throw new Error("Not authenticated");

            // `UpdateProfileInput` is an interface, so it carries no index
            // signature — the shim takes a record because it serialises to JSON.
            const { data, error: updateError } = await db.from("profiles")
                .update(updates as Record<string, unknown>)
                .eq("id", userId)
                .select()
                .single();

            if (updateError) throw updateError;
            return data as Profile;
        },
        onSuccess: (updatedProfile) => {
            // Update cache immediately
            queryClient.setQueryData(coreQueryKeys.profile(userId), updatedProfile);
        },
    });

    // Wrapper for updateProfile to maintain backward compatibility
    const updateProfile = useCallback(async (updates: UpdateProfileInput): Promise<Profile> => {
        return updateProfileMutation(updates);
    }, [updateProfileMutation]);

    // Wrapper for refreshProfile to maintain backward compatibility
    const refreshProfile = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: coreQueryKeys.profile(userId) });
    }, [queryClient, userId]);

    // Error handling - convert query error to Error object
    const error = queryError instanceof Error ? queryError : 
                  queryError ? new Error("Failed to fetch profile") : null;

    // Clear the cached profile when not authenticated
    useEffect(() => {
        if (!isAuthenticated || !userId) {
            queryClient.setQueryData(coreQueryKeys.profile(userId), null);
        }
    }, [isAuthenticated, userId, queryClient]);

    const value: ProfileContextType = {
        profile,
        isLoading,
        error,
        updateProfile,
        isUpdating,
        refreshProfile,
    };

    return (
        <ProfileContext.Provider value={value}>
            {children}
        </ProfileContext.Provider>
    );
};
